import { useState, useCallback } from 'react';

/**
 * useLocalStorage - Persist state to localStorage
 *
 * Replaces the common pattern of:
 *   const [val, setVal] = useState(() => localStorage.getItem(key));
 *   useEffect(() => localStorage.setItem(key, val), [val]);
 *
 * Values are stored as JSON. Handles legacy raw-string values gracefully.
 */
export default function useLocalStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return defaultValue;
      return JSON.parse(item);
    } catch {
      // Handle legacy values stored without JSON.stringify
      // e.g. 'cards' instead of '"cards"'
      return localStorage.getItem(key) ?? defaultValue;
    }
  });

  const setStoredValue = useCallback((newValue) => {
    setValue(prev => {
      const resolved = typeof newValue === 'function' ? newValue(prev) : newValue;
      try {
        localStorage.setItem(key, JSON.stringify(resolved));
      } catch {
        // localStorage full or unavailable
      }
      return resolved;
    });
  }, [key]);

  return [value, setStoredValue];
}
